import ElakayImage from '../../components/images/ElakayImage';
// <DynamicSwiper> — the ONE renderer of a Dynamic Swiper, used on real pages
// (through PageDynamicSwipers / DynamicPageRenderer), in the Swiper Manager
// preview and in the editor's live preview, so what Family sees while
// editing is exactly what visitors get.
//
// Layout contract: the frame takes the FIRST slide's aspect ratio
// (swiper.aspectRatio) through CSS aspect-ratio, so its height is known
// before any image loads and never changes between slides; every image is
// drawn inside that frame with its own object-fit / object-position (never
// stretched). A max height never distorts the ratio: it narrows the frame
// (centered) instead.
//
// Responsiveness follows the frame's REAL size (measured on the element,
// not the viewport): its height decides how much text fits (see
// useFrameBand), so the same swiper adapts inside a page column, on a phone
// or in the scaled editor preview.
//
// Two kinds of slides share this renderer: the original IMAGE slide (one
// image + title / subtitle / description / button) and the Swiper Studio
// CANVAS slide (background + positioned elements, drawn by
// <SwiperCanvasSlide>). Tablet / Mobile may use their own frame shape
// (settings.tabletRatio / mobileRatio), otherwise every device keeps the
// swiper's shape, as before.
//
// Advanced display (settings.display, swiperDisplayModel.js): when it is
// enabled the frame is wrapped in an outer layer (external margin, width
// mode) and a box (padding, border, radius, background, shadow), with
// Desktop / Tablet / Mobile values. When it is null or off, the markup and
// styles below are exactly the historical ones.
import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { toImageUrl } from "../../config";
import { elakayAutoText } from "../pageInside/dataElakay";
import useElakaySwiper from "../slider/swiperSettings/useElakaySwiper";
import {
  DEFAULT_SWIPER_SETTINGS, SLIDE_OVERLAYS, isInternalPath, isSafeButtonUrl, slideImageBoxStyle, slideImageStyle,
} from "./dynamicSwiperRegistry";
import { frameRatioFor, isCanvasSlide, layoutDeviceFor } from "./swiperCanvasModel";
import SwiperCanvasSlide from "./SwiperCanvasSlide";
import { displayLayout, radiusCss, resolveDisplayBox, sanitizeDisplay, sanitizeOptionalRadius } from "./swiperDisplayModel";
import {
  DiagnosticMargin, DiagnosticPadding, breakoutStyle, useDisplayMetrics, useViewportBreakout,
} from "./swiperDisplayRuntime";
import "./DynamicSwipers.css";

const MOBILE_QUERY = "(max-width: 575.98px)";
const TABLET_QUERY = "(max-width: 991.98px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const matches = (query) => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;

const useMediaQuery = (query, enabled = true) => {
  const [value, setValue] = useState(() => (enabled ? matches(query) : false));
  useEffect(() => {
    if (!enabled || typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(query);
    const update = () => setValue(media.matches);
    update();
    if (media.addEventListener) media.addEventListener("change", update);
    else media.addListener(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", update);
      else media.removeListener(update);
    };
  }, [query, enabled]);
  return value;
};

// Elakay's platform-wide "animations disabled" mode (Animation Settings ->
// globalAnimations, applied by Routes.js as a class on <html>) forces
// `transition: none !important` on every element. Swiper then never gets the
// transitionend it waits for and freezes after the first slide change, so in
// that mode slides change instantly (speed 0 completes synchronously).
const ANIMATIONS_DISABLED_CLASS = "elakay-animations-disabled";
const readAnimationsDisabled = () => typeof document !== "undefined"
  && document.documentElement.classList.contains(ANIMATIONS_DISABLED_CLASS);

const useAnimationsDisabled = () => {
  const [disabled, setDisabled] = useState(readAnimationsDisabled);
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => setDisabled(readAnimationsDisabled()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    setDisabled(readAnimationsDisabled());
    return () => observer.disconnect();
  }, []);
  return disabled;
};

// device: "auto" (real viewport) or a forced "desktop" | "tablet" | "mobile"
// (editor / manager previews). Only decides which arrows / max height
// setting applies; text sizing follows the frame itself.
const useDevice = (device) => {
  const auto = device === "auto" || !device;
  const isMobile = useMediaQuery(MOBILE_QUERY, auto);
  const isTablet = useMediaQuery(TABLET_QUERY, auto);
  if (!auto) return device;
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

// Height bands of the frame (px): below 96 only the button fits, below 160
// title + button, below 260 no description, above that everything.
const bandFor = (height) => {
  if (height < 96) return "xs";
  if (height < 160) return "sm";
  if (height < 260) return "md";
  return "lg";
};
// First-paint guess when the element cannot be measured (no ResizeObserver).
const ESTIMATED_FRAME_WIDTH = { mobile: 360, tablet: 720, desktop: 1200 };

// measuredHeight: the frame's height is NOT width / ratio (Advanced display
// custom / full height, or a capped fill width): use its real height.
// Also returns the frame's REAL ratio once measured (null before): a
// "Contain" image with rounded corners is sized to the visible picture.
const useFrameBand = (frameRef, ratio, device, measuredHeight = false) => {
  const [band, setBand] = useState(() => bandFor((ESTIMATED_FRAME_WIDTH[device] || 1200) / ratio));
  const [measuredRatio, setMeasuredRatio] = useState(null);
  // Layout effect + ResizeObserver: the first measurement lands before the
  // first paint, so the text never visibly reflows on load.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const estimate = () => setBand(bandFor((ESTIMATED_FRAME_WIDTH[device] || 1200) / ratio));
    if (!frame || typeof ResizeObserver === "undefined") {
      estimate();
      return undefined;
    }
    const update = () => {
      const width = frame.clientWidth;
      const height = measuredHeight ? frame.clientHeight : 0;
      if (height) setBand(bandFor(height));
      else if (width) setBand(bandFor(width / ratio));
      else estimate();
      const realRatio = width && frame.clientHeight ? Math.round((width / frame.clientHeight) * 10000) / 10000 : null;
      setMeasuredRatio((prev) => (prev === realRatio ? prev : realRatio));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frameRef, ratio, device, measuredHeight]);
  return { band, measuredRatio };
};

const SlideButton = ({ button, onNavigate }) => {
  if (!button || !button.label || !isSafeButtonUrl(button.url)) return null;
  const className = `dsw-button dsw-button-${button.style || "primary"}`;
  const label = <span className="dsw-button-label">{button.label}</span>;
  const newTab = button.target === "_blank";
  if (isInternalPath(button.url) && !newTab) {
    return <Link className={className} to={button.url} onClick={onNavigate}>{label}</Link>;
  }
  if (newTab) {
    return (
      <a className={className} href={button.url} target="_blank" rel="noopener noreferrer">
        {label}
        <span className="dsw-visually-hidden">{elakayAutoText("h_dsw_opens_new_tab", " (opens in a new tab)")}</span>
      </a>
    );
  }
  // External link, same tab.
  return <a className={className} href={button.url} rel="noopener noreferrer">{label}</a>;
};

const SlideContent = ({ slide, onNavigate }) => {
  const hasText = Boolean(slide.title || slide.subtitle || slide.description);
  const hasButton = Boolean(slide.button && slide.button.label);
  const overlay = SLIDE_OVERLAYS.includes(slide.overlay) ? slide.overlay : "auto";
  // A "full" overlay tints the image even without any text on it.
  if (!hasText && !hasButton && overlay !== "full") return null;
  const position = slide.contentPosition || "bottom-left";
  return (
    <div className={`dsw-content dsw-pos-${position} dsw-theme-${slide.textTheme || "light"} dsw-overlay-${overlay}`}>
      {(hasText || hasButton) && (
        <div className="dsw-content-inner">
          {slide.title && <p className="dsw-title">{slide.title}</p>}
          {slide.subtitle && <p className="dsw-subtitle">{slide.subtitle}</p>}
          {slide.description && <p className="dsw-description">{slide.description}</p>}
          {hasButton && <SlideButton button={slide.button} onNavigate={onNavigate} />}
        </div>
      )}
    </div>
  );
};

// priority: "high" (first image of the page's first swiper), "normal"
// (visible slide of another swiper: native lazy loading) or "later" (a
// slide that is not visible yet: no image request until it is shown).
// imageRadius: CSS border-radius of this slide's image ("" = square);
// frameRatio: the frame's real width / height.
const SlideImage = ({ slide, settings, priority, imageRadius = "", frameRatio = 0 }) => {
  const [failed, setFailed] = useState(false);
  const src = slide.previewSrc || toImageUrl(slide.imageUrl);
  useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) return <div className="dsw-image-fallback" aria-hidden="true" />;
  const imageStyle = imageRadius ? slideImageBoxStyle(slide, settings, frameRatio, imageRadius) : slideImageStyle(slide, settings);
  if (priority === "later") return <span className="dsw-image" style={imageStyle} aria-hidden="true" />;
  const eager = priority === "high";
  return (
    <ElakayImage
      preset="banner"
      className="dsw-image"
      image={{ url: src, mimeType: slide.mimeType || slide.imageMetadata?.mimeType }}
      alt={slide.alt || ""}
      width={slide.width || undefined}
      height={slide.height || undefined}
      loading={eager ? "eager" : "lazy"}
      // React 18 does not know fetchPriority yet; the lowercase DOM
      // attribute is what browsers read.
      fetchpriority={priority === "high" ? "high" : priority === "later" ? "low" : "auto"}
      decoding="async"
      draggable={false}
      style={imageStyle}
      onError={() => setFailed(true)}
    />
  );
};

const renderableSlide = (slide) => Boolean(slide && (isCanvasSlide(slide) || slide.imageUrl || slide.previewSrc));

// Swiper Studio's per-instance choices remain the highest-priority layer of
// the managed Swiper options; fields the author never touched inherit the
// Family global default. The 2026-10-15 choices (paginationType ...
// stopOnLastSlide) are null until chosen: then the historical value is kept
// (pagination "bullets", keyboard on, autoplay never stopped by an
// interaction) or, for options this component never set, the Family default.
export const instanceOptionsFor = (settings, device) => {
  const chosen = (key) => settings[key] !== null && settings[key] !== undefined;
  return { [device]: {
    effect: {
      type: settings.transition === "fade" ? "fade" : "slide",
      speed: settings.speed,
      loop: settings.loop,
      grabCursor: true,
      fadeCrossFade: true,
      ...(chosen("rewind") ? { rewind: settings.rewind } : {}),
    },
    layout: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 0 },
    navigation: { enabled: device === "mobile" ? settings.showArrowsMobile : settings.showArrowsDesktop },
    pagination: {
      enabled: settings.showPagination,
      type: chosen("paginationType") ? settings.paginationType : "bullets",
      clickable: true,
      ...(chosen("dynamicBullets") ? { dynamicBullets: settings.dynamicBullets } : {}),
    },
    autoplay: {
      enabled: settings.autoplay,
      delay: settings.autoplayDelay,
      disableOnInteraction: chosen("disableOnInteraction") ? settings.disableOnInteraction : false,
      pauseOnMouseEnter: settings.pauseOnHover !== false,
      ...(chosen("stopOnLastSlide") ? { stopOnLastSlide: settings.stopOnLastSlide } : {}),
    },
    keyboard: { enabled: chosen("keyboard") ? settings.keyboard : true, onlyInViewport: true },
    ...(chosen("allowTouchMove") ? { touch: { allowTouchMove: settings.allowTouchMove } } : {}),
  } };
};

// showHidden: render a note instead of nothing when Advanced display hides
// the swiper on this device (Studio / Manager previews); diagnostics: draw
// the margin / padding / frame overlay (Studio "Show spacing").
const DynamicSwiper = ({
  swiper, device = "auto", eager = false, className = "", label, onNavigate, activeIndex, replayToken = 0,
  showHidden = false, diagnostics = false,
}) => {
  const currentDevice = useDevice(device);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const animationsDisabled = useAnimationsDisabled();
  const instant = reducedMotion || animationsDisabled;
  const settings = useMemo(() => ({ ...DEFAULT_SWIPER_SETTINGS, ...(swiper?.settings || {}) }), [swiper?.settings]);
  const slides = useMemo(() => (Array.isArray(swiper?.slides) ? swiper.slides.filter(renderableSlide) : []), [swiper?.slides]);
  const deviceFrame = frameRatioFor(swiper, currentDevice);
  const width = Number(deviceFrame?.width) || Number(slides[0]?.width) || 16;
  const height = Number(deviceFrame?.height) || Number(slides[0]?.height) || 9;
  const ratio = width / height;

  const instanceOptions = useMemo(() => instanceOptionsFor(settings, currentDevice), [currentDevice, settings]);
  const managed = useElakaySwiper("dynamicSwiper", {
    swiperOptions: instanceOptions,
    slideCount: slides.length,
    autoNavigation: true,
    deviceOverride: currentDevice,
  });

  // Re-sanitized here too (defense in depth: only bounded numbers, enums and
  // hex colors ever reach a style), whatever the source of the swiper.
  const display = useMemo(() => sanitizeDisplay(settings.display, settings), [settings]);
  const displayBox = useMemo(() => resolveDisplayBox(display, currentDevice), [display, currentDevice]);
  const advanced = Boolean(displayBox);
  const layout = advanced ? displayLayout(displayBox, { width, height }) : null;
  const outerRef = useRef(null);
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const breakout = useViewportBreakout(outerRef, advanced && displayBox.size.widthMode === "full");
  const metrics = useDisplayMetrics(outerRef, boxRef, advanced && diagnostics, [displayBox]);
  const { band, measuredRatio } = useFrameBand(
    frameRef, ratio, currentDevice,
    advanced && (displayBox.size.heightMode !== "auto" || (displayBox.size.maxHeight && ["fill", "full"].includes(displayBox.size.widthMode))),
  );
  const [instance, setInstance] = useState(null);
  // Index (in `slides`) of the visible slide: canvas videos play and
  // "slide" animations run only there.
  const [visibleIndex, setVisibleIndex] = useState(() => (activeIndex !== undefined && activeIndex !== null
    ? Math.max(0, Math.min(Math.max(slides.length - 1, 0), activeIndex))
    : 0));

  const isMobile = currentDevice === "mobile";
  const maxHeight = isMobile ? settings.maxHeightMobile : settings.maxHeightDesktop;
  const showArrows = slides.length > 1 && Boolean(managed.swiperProps.navigation);
  const showPagination = slides.length > 1 && Boolean(managed.swiperProps.pagination);

  // Editor preview only: follow the slide being edited.
  useEffect(() => {
    if (!instance || instance.destroyed || activeIndex === undefined || activeIndex === null) return;
    const target = Math.max(0, Math.min(slides.length - 1, activeIndex));
    if (instance.realIndex !== target) instance.slideToLoop(target, instant ? 0 : settings.speed);
  }, [instance, activeIndex, slides.length, instant, settings.speed]);

  const frameStyle = advanced
    ? { ...layout.frameStyle, "--dsw-ratio": ratio, backgroundColor: "transparent" }
    : {
      aspectRatio: `${width} / ${height}`,
      "--dsw-ratio": ratio,
      borderRadius: `${settings.borderRadius ?? 12}px`,
      backgroundColor: settings.backgroundColor || DEFAULT_SWIPER_SETTINGS.backgroundColor,
    };
  const rootStyle = advanced
    ? { ...layout.outerStyle, ...breakoutStyle(breakout) }
    : (maxHeight ? { maxWidth: `min(100%, ${Math.round(maxHeight * ratio)}px)` } : undefined);
  const regionLabel = label || elakayAutoText("h_dsw_region_label", "Promotions");

  if (!slides.length) return null;
  if (advanced && displayBox.visible === false) {
    return showHidden ? (
      <div className="dsw-hidden-note" role="note">
        {elakayAutoText("h_dsw_hidden_on_device", "Hidden on this device (Advanced display → Visibility).")}
      </div>
    ) : null;
  }

  const classes = [
    "dsw",
    `dsw-device-${currentDevice}`,
    `dsw-h-${band}`,
    showArrows ? "dsw-has-arrows" : "",
    showPagination ? "dsw-has-pagination" : "",
    advanced ? layout.outerClass : "",
    advanced && diagnostics ? "dsw-diag-on" : "",
    className,
  ].filter(Boolean).join(" ");
  const diagnosticLabels = diagnostics ? {
    margin: elakayAutoText("h_dsw_diag_margin_short", "M"),
    padding: elakayAutoText("h_dsw_diag_padding_short", "P"),
  } : null;
  // Legacy: <section><frame/></section>. Advanced: <section><box><frame/></box></section>.
  const shell = (frame, extraProps = {}) => (
    <section className={classes} style={rootStyle} aria-label={regionLabel} ref={outerRef} {...extraProps}>
      {advanced ? (
        <div className="dsw-box" style={layout.boxStyle} ref={boxRef}>
          {frame}
          {diagnostics && <DiagnosticPadding metrics={metrics} label={diagnosticLabels.padding} />}
        </div>
      ) : frame}
      {advanced && diagnostics && <DiagnosticMargin metrics={metrics} label={diagnosticLabels.margin} />}
    </section>
  );

  const firstPriority = eager ? "high" : "normal";
  const animate = !instant;

  // Corners: the slide's own value, else the "all slides / all images" one
  // (an own value of 0 = square even when the others are rounded).
  const allSlidesRadius = sanitizeOptionalRadius(settings.slideRadius);
  const allImagesRadius = sanitizeOptionalRadius(settings.imageRadius);
  const slideStyle = (slide) => {
    const radius = radiusCss(sanitizeOptionalRadius(slide.cornerRadius) || allSlidesRadius);
    return radius ? { borderRadius: radius, overflow: "hidden" } : undefined;
  };
  const imageRadiusOf = (slide) => radiusCss(sanitizeOptionalRadius(slide.imageRadius) || allImagesRadius);
  const frameRatio = measuredRatio || ratio;

  const renderSlide = (slide, index, priority) => (isCanvasSlide(slide) ? (
    <SwiperCanvasSlide
      slide={slide}
      device={layoutDeviceFor(settings, currentDevice)}
      active={index === visibleIndex}
      animate={animate}
      eager={priority === "high"}
      onNavigate={onNavigate}
      replayToken={replayToken}
    />
  ) : (
    <>
      <SlideImage slide={slide} settings={settings} priority={priority} imageRadius={imageRadiusOf(slide)} frameRatio={frameRatio} />
      <SlideContent slide={slide} onNavigate={onNavigate} />
    </>
  ));

  if (slides.length === 1) {
    return shell(
      <div className="dsw-frame" style={frameStyle} ref={frameRef}>
        <div className="dsw-slide" style={slideStyle(slides[0])}>
          {renderSlide(slides[0], 0, firstPriority)}
        </div>
      </div>,
    );
  }

  const autoplay = managed.swiperProps.autoplay && !reducedMotion
    ? managed.swiperProps.autoplay
    : false;
  const startIndex = activeIndex !== undefined && activeIndex !== null
    ? Math.max(0, Math.min(slides.length - 1, activeIndex))
    : 0;

  return shell(
    (
      <div className="dsw-frame" style={frameStyle} ref={frameRef}>
        <Swiper
          // Re-init when a structural option changes (preview toggles);
          // the library does not diff these props on its own.
          key={`${managed.key}-${showArrows}-${showPagination}-${Boolean(autoplay)}-${instant}-${slides.length}-${managed.swiperProps.allowTouchMove}-${JSON.stringify(autoplay || null)}`}
          {...managed.swiperProps}
          className={`dsw-swiper ${managed.className}`}
          style={managed.style}
          initialSlide={startIndex}
          speed={instant ? 0 : managed.swiperProps.speed}
          autoplay={autoplay}
          onSwiper={setInstance}
          onSlideChange={(current) => setVisibleIndex(current.realIndex)}
          a11y={{
            enabled: true,
            prevSlideMessage: elakayAutoText("h_dsw_prev_slide", "Previous slide"),
            nextSlideMessage: elakayAutoText("h_dsw_next_slide", "Next slide"),
            paginationBulletMessage: elakayAutoText("h_dsw_go_to_slide", "Go to slide {{index}}"),
            slideLabelMessage: elakayAutoText("h_dsw_slide_label", "{{index}} / {{slidesLength}}"),
          }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id || index}>
              <div className="dsw-slide" style={slideStyle(slide)}>
                {renderSlide(slide, index, index === visibleIndex ? (index === startIndex ? firstPriority : "normal") : "later")}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    ),
    { "aria-roledescription": "carousel" },
  );
};

export default memo(DynamicSwiper);
